/**
 * The role tinyint **as the existing consumer backend serialises it on `GET /me`**,
 * where `users.role` is a real column. Only the two staff roles use this console.
 *
 * This is NOT the shape the admin endpoints use. `GET /admin/users` and
 * `/admin/users/{id}` send the role *code* as a string, because the console
 * derives role by joining `staff_user_roles` → `staff_roles.code` and never reads a
 * `users.role` column — Requirement 2 is explicit that no such column exists for
 * its purposes. Conflating the two is what previously left `AdminUser.role` typed
 * as a number, which would have broken the moment anything rendered or filtered
 * on it. See `features/users/api.ts`.
 *
 * NOTE: RBAC does not exist yet, and this shape is not settled — if the admin
 * service owns staff identity, `users.role` is never added and staff live in
 * `cwc_admin.staff` instead. See docs/internal-ops-console-phase1-spec.md §3.1
 * and §9 Q2. Everything here is presentation only; the backend is the security
 * boundary — a hidden nav item is UX, not protection.
 */
export const ROLE = {
  MEMBER: 1,
  OPS_ADMIN: 2,
  SUPER_ADMIN: 3,
} as const

export type RoleValue = (typeof ROLE)[keyof typeof ROLE]

/** Staff-only slugs used throughout the UI. Members never reach this console. */
export type Role = 'ops' | 'super'

export const ROLE_LABEL: Record<Role, string> = {
  ops: 'Operations Admin',
  super: 'Super Admin',
}

/** Maps the wire tinyint to a UI slug; null for members and unknown values. */
export function toRole(value: number): Role | null {
  if (value === ROLE.OPS_ADMIN) return 'ops'
  if (value === ROLE.SUPER_ADMIN) return 'super'
  return null
}

/** `UserDto` as the backend serialises it (docs/auth/api-contract.md). */
export interface UserDto {
  /** = users.public_id, an opaque string — NOT the numeric PK. */
  id: string
  display_name: string
  avatar_url: string
  role: number
  identities: Array<{
    provider: string
    type: string
    email: string
  }>
}

/** The signed-in staff member, normalised for UI use. */
export interface CurrentUser {
  id: string
  displayName: string
  avatarUrl: string
  /** Sourced from auth_identities — `users` has no email column by design. */
  email: string
  role: Role
}
